import { escapeXml, buildXmlPayload, sendXmlRequest } from './core';

// ============ TRANSACTION RULE CONFIGURATION ============
const TRANSACTION_RULE_PARENT_FIELDS = ['NAME', 'JOURNALID', 'DESCRIPTION'];
const TRANSACTION_RULE_EXCLUDED_FIELDS = ['RECORDNO', 'WHENCREATED', 'WHENMODIFIED', 'CREATEDBY', 'MODIFIEDBY', 'NAME', 'JOURNALID', 'DESCRIPTION'];

// ============ DETECTION ============
export const isTransactionRuleType = (objectName) => {
  return objectName?.toUpperCase() === 'TRANSACTIONRULE';
};

// ============ FUNCTIONS ============
export const getTransactionRuleCreateSummary = (records) => {
  const grouped = {};
  const missingName = [];
  
  records.forEach((rec, idx) => {
    const name = rec.NAME;
    if (!name) { missingName.push(idx + 2); return; }
    if (!grouped[name]) grouped[name] = { details: 0, hasJournalId: false };
    grouped[name].details++;
    if (rec.JOURNALID) grouped[name].hasJournalId = true;
  });
  
  const missingJournalId = Object.entries(grouped).filter(([name, info]) => !info.hasJournalId).map(([name]) => name);
  
  return {
    transactionRules: Object.entries(grouped).map(([name, info]) => ({ name, detailCount: info.details })),
    missingNameRows: missingName,
    missingJournalIdRules: missingJournalId,
    totalDetails: records.length,
    totalRules: Object.keys(grouped).length
  };
};

export const buildTransactionRulePreviewXml = (records) => {
  const grouped = {};
  records.forEach(rec => {
    const name = rec.NAME;
    if (!name) return;
    if (!grouped[name]) grouped[name] = [];
    grouped[name].push(rec);
  });
  
  const ruleNames = Object.keys(grouped);
  if (ruleNames.length === 0) return '<!-- No valid records with NAME -->';
  
  const firstName = ruleNames[0];
  const firstGroupRecords = grouped[firstName];
  
  const parentFields = {};
  TRANSACTION_RULE_PARENT_FIELDS.forEach(field => {
    const val = firstGroupRecords[0]?.[field];
    if (val !== undefined && val !== '' && val !== null) parentFields[field] = val;
  });
  const parentXml = Object.entries(parentFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('\n      ');
  
  const sampleDetails = firstGroupRecords.slice(0, 2);
  const detailsXml = sampleDetails.map(detail => {
    const detailFields = Object.entries(detail)
      .filter(([k, v]) => !TRANSACTION_RULE_EXCLUDED_FIELDS.includes(k.toUpperCase()) && v !== '' && v !== undefined && v !== null)
      .map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`)
      .join('\n          ');
    return `<TRANSACTIONRULEDETAIL>\n          ${detailFields}\n        </TRANSACTIONRULEDETAIL>`;
  }).join('\n        ');
  
  const moreDetailsNote = firstGroupRecords.length > 2 ? `\n        <!-- ... and ${firstGroupRecords.length - 2} more detail(s) ... -->` : '';
  const moreRulesNote = ruleNames.length > 1 ? `\n\n<!-- ... and ${ruleNames.length - 1} more transaction rule(s) will be created ... -->` : '';
  
  return `<function controlid="${escapeXml(firstName)}">
  <create>
    <TRANSACTIONRULE>
      ${parentXml}
      <TRANSACTIONRULEDETAILS>
        ${detailsXml}${moreDetailsNote}
      </TRANSACTIONRULEDETAILS>
    </TRANSACTIONRULE>
  </create>
</function>${moreRulesNote}`;
};

export const createTransactionRules = async (config, records) => {
  const missingName = records.filter(r => !r.NAME);
  if (missingName.length > 0) throw new Error(`${missingName.length} record(s) missing NAME.`);
  
  const grouped = {};
  records.forEach(rec => {
    const name = rec.NAME;
    if (!grouped[name]) grouped[name] = [];
    grouped[name].push(rec);
  });
  
  for (const [name, details] of Object.entries(grouped)) {
    const hasJournalId = details.some(d => d.JOURNALID);
    if (!hasJournalId) throw new Error(`Transaction rule '${name}' is missing required JOURNALID field.`);
  }
  
  const functions = Object.entries(grouped).map(([name, details]) => {
    const parentFields = {};
    TRANSACTION_RULE_PARENT_FIELDS.forEach(field => {
      const val = details[0][field];
      if (val !== undefined && val !== '' && val !== null) parentFields[field] = val;
    });
    
    const detailsXml = details.map(detail => {
      const detailFields = {};
      Object.entries(detail).forEach(([key, val]) => {
        if (TRANSACTION_RULE_EXCLUDED_FIELDS.includes(key.toUpperCase())) return;
        if (val === '' || val === undefined || val === null) return;
        detailFields[key] = val;
      });
      const fieldsXml = Object.entries(detailFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('');
      return `<TRANSACTIONRULEDETAIL>${fieldsXml}</TRANSACTIONRULEDETAIL>`;
    }).join('');
    
    const parentXml = Object.entries(parentFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('');
    return `<function controlid="${escapeXml(String(name))}"><create><TRANSACTIONRULE>${parentXml}<TRANSACTIONRULEDETAILS>${detailsXml}</TRANSACTIONRULEDETAILS></TRANSACTIONRULE></create></function>`;
  }).join('');
  
  const xmlPayload = buildXmlPayload(config, functions);
  const result = await sendXmlRequest(config, 'TRANSACTIONRULE', xmlPayload, 'TRANSACTION RULE CREATE');
  return result.results;
};
